import type React from 'react';
import { type FC, useRef, useState } from 'react';

import { _isMobile, isValidResultUrl } from '../helper';
import { Hvc } from '../hooks';
import { AlertSVG, CameraSVG, CheckSVG, EyeSVG, PlusSVG, RefreshSVG, TrashSVG, UploadSVG } from '../svgs';
import TextPrompt from '../text-prompt';

export type UploadPhase = 'pending' | 'uploading' | 'success' | 'error';

export interface IFileUploadStatus {
	phase: UploadPhase;
	progress: number;
	url?: string;
	error?: string;
}

// Stable per-file id (name + size) — survives reindexing when files are added/removed
// mid-flight, so a card's status never gets attributed to the wrong sheet.
export const fileKey = (file: File) => `${file.name}-${file.size}`;

export interface IMUC {
	setFiles: (files: File[]) => void;
	handlePreviewFile?: (index: number) => void;
	files: File[];
	uploadDocs?: () => void;
	load?: boolean;
	isUpdate?: boolean;
	isUploaded?: boolean;
	uploadedFiles: string[];
	fileStatuses?: Record<string, IFileUploadStatus>;
	onRetry?: (index: number) => void;
	captureTitle?: string;
	captureSubtitle?: string;
}

export const MultipleMediaUploadComponent: FC<IMUC> = ({
	setFiles,
	files,
	handlePreviewFile,
	load,
	uploadedFiles,
	fileStatuses,
	onRetry,
	captureTitle = 'Capture Election Results',
	captureSubtitle = 'Take a clear photo of the result sheet',
}) => {
	const cameraInputRef = useRef<HTMLInputElement>(null);
	const browseInputRef = useRef<HTMLInputElement>(null);
	const [fileError, setFileError] = useState<string>('');
	const [isDragOver, setIsDragOver] = useState(false);
	const [removingIndex, setRemovingIndex] = useState<number | null>(null);

	const ismobile = _isMobile();
	const isPreview = typeof handlePreviewFile === 'function';

	// Uploading is in progress whenever any card is mid-flight (or the batch flag is set).
	// We lock add/remove during this window so the name+size-keyed status map stays consistent.
	const anyUploading = !!load || Object.values(fileStatuses || {}).some((s) => s?.phase === 'uploading');

	const validateFiles = (newFiles: File[]) => {
		setFileError('');
		const validFiles = newFiles.filter((file) => {
			if (!file.type.includes('image')) {
				setFileError('Use png / jpg / jpeg format for this activity.');
				return false;
			}
			if (file.size > 10000000) {
				setFileError('File size should be 10mb or less');
				return false;
			}
			return true;
		});
		return validFiles;
	};

	const handleOnChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		const { files: selectedFiles } = target;
		if (selectedFiles?.length) {
			const newFiles = Array.from(selectedFiles);
			const validFiles = validateFiles(newFiles);
			if (validFiles.length > 0) {
				setFiles([...files, ...validFiles]);
			}
		}
		// Reset input so the same file can be re-selected
		if (target) target.value = '';
	};

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragOver(false);
		if (anyUploading) return;
		const droppedFiles = Array.from(event.dataTransfer.files);
		const validFiles = validateFiles(droppedFiles);
		if (validFiles.length > 0) {
			setFiles([...files, ...validFiles]);
		}
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
	};

	const handleRemoveFile = (index: number) => {
		if (anyUploading) return;
		setRemovingIndex(index);
		setTimeout(() => {
			const prevFiles = [...files];
			setFiles(prevFiles.filter((_, i) => i !== index));
			setRemovingIndex(null);
		}, 250);
	};

	const openCamera = () => {
		if (anyUploading) return;
		cameraInputRef.current?.click();
	};

	const openBrowse = () => {
		if (anyUploading) return;
		browseInputRef.current?.click();
	};

	const getImageSrc = (index: number) => {
		try {
			return URL.createObjectURL(files[index]);
		} catch {
			return uploadedFiles[index];
		}
	};

	const formatFileSize = (bytes: number) => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / 1048576).toFixed(2)} MB`;
	};

	const phaseLabel: Record<UploadPhase, string> = {
		pending: 'Ready to upload',
		uploading: 'Uploading…',
		success: 'Uploaded & verified',
		error: 'Upload failed',
	};

	return (
		<div className='w-100'>
			{/* Hidden file inputs */}
			<input
				type='file'
				accept='image/*'
				capture='environment'
				ref={cameraInputRef}
				className='upload-result__hidden-input'
				onChange={handleOnChange}
				aria-label='Take photo with camera'
			/>
			<input
				type='file'
				multiple
				accept='image/*'
				ref={browseInputRef}
				className='upload-result__hidden-input'
				onChange={handleOnChange}
				aria-label='Browse files from device'
			/>

			{/* Empty State: Camera-First Capture Zone */}
			<Hvc
				view={!files.length}
				removeDOM
			>
				<div className='upload-result__capture-zone'>
					<div className='upload-result__capture-illustration'>
						<CameraSVG />
					</div>
					<h3 className='upload-result__capture-title'>{captureTitle}</h3>
					<p className='upload-result__capture-subtitle'>{captureSubtitle}</p>
					<button
						className='upload-result__camera-btn'
						onClick={openCamera}
						type='button'
						aria-label='Open camera to take photo'
					>
						<CameraSVG color='#fff' />
						<span>Take Photo</span>
					</button>

					{/* Desktop drag-drop zone */}
					{!ismobile && (
						<div
							className={`upload-result__drop-zone${isDragOver ? ' upload-result__drop-zone--active' : ''}`}
							onDragOver={handleDragOver}
							onDragEnter={() => setIsDragOver(true)}
							onDragLeave={() => setIsDragOver(false)}
							onDrop={handleDrop}
						>
							<UploadSVG />
							<p>Drag and drop files here</p>
						</div>
					)}

					<button
						className='upload-result__browse-btn'
						onClick={openBrowse}
						type='button'
					>
						or browse from device
					</button>
				</div>
			</Hvc>

			{/* Files Added: Preview Cards */}
			<Hvc
				removeDOM
				view={!!files.length}
				className='f-column-20'
			>
				{/* File counter + add more */}
				<div className='f-row justify-content-between align-items-center'>
					<div className='upload-result__file-counter'>
						<span>{files.length}</span>
						file{files.length !== 1 ? 's' : ''} added
					</div>
					<div className='upload-result__add-more'>
						<button
							onClick={openCamera}
							type='button'
							className='upload-result__add-camera'
							aria-label='Take another photo'
							disabled={anyUploading}
						>
							<CameraSVG />
							{!ismobile && 'Take photo'}
						</button>
						<button
							onClick={openBrowse}
							type='button'
							className='upload-result__add-browse'
							aria-label='Browse more files'
							disabled={anyUploading}
						>
							<PlusSVG />
							{!ismobile && 'Browse'}
						</button>
					</div>
				</div>

				{/* Preview cards */}
				<div
					className='upload-result__previews'
					aria-live='polite'
				>
					{files.map((file, index) => {
						const status = fileStatuses?.[fileKey(file)];
						// Live status wins; otherwise fall back to the persisted upload result
						// (uploadedFiles is index-aligned with files) so the verified badge
						// survives navigating away from and back to this step.
						const phase: UploadPhase = status?.phase ?? (isValidResultUrl(uploadedFiles?.[index]) ? 'success' : 'pending');
						const isUploadingFile = phase === 'uploading';
						const isErrorFile = phase === 'error';
						const isSuccessFile = phase === 'success';
						return (
							<div
								key={`${file.name}-${file.size}-${index}`}
								className={[
									'upload-result__preview-card',
									`upload-result__preview-card--${phase}`,
									removingIndex === index ? 'upload-result__preview-card--exiting' : '',
								]
									.filter(Boolean)
									.join(' ')}
							>
								<div className='upload-result__preview-thumb'>
									<img
										src={getImageSrc(index)}
										alt={`Result sheet ${index + 1}`}
									/>
									{isSuccessFile && (
										<span
											className='upload-result__preview-verified'
											aria-hidden='true'
										>
											<CheckSVG
												color='#fff'
												width='14'
												height='14'
											/>
										</span>
									)}
								</div>
								<div className='upload-result__preview-info'>
									<p className='upload-result__preview-name'>{file.name}</p>
									<p className='upload-result__preview-size'>{formatFileSize(file.size)}</p>

									{/* Status chip */}
									<span className={`upload-result__status-chip upload-result__status-chip--${phase}`}>
										{isSuccessFile && (
											<CheckSVG
												color='var(--success-600)'
												width='12'
												height='12'
											/>
										)}
										{isErrorFile && <AlertSVG />}
										{phaseLabel[phase]}
									</span>

									{/* Per-card progress while uploading */}
									{isUploadingFile && (
										<div className='upload-result__card-progress'>
											<div
												className='upload-result__card-progress-bar'
												style={{ width: `${status?.progress || 0}%` }}
												role='progressbar'
												aria-valuenow={status?.progress || 0}
												aria-valuemin={0}
												aria-valuemax={100}
												aria-label={`Uploading ${file.name}`}
											/>
										</div>
									)}

									{/* Inline error with retry */}
									{isErrorFile && (
										<div
											className='upload-result__card-error'
											aria-live='assertive'
										>
											<span>{status?.error || 'Upload failed.'}</span>
											{typeof onRetry === 'function' && (
												<button
													type='button'
													className='upload-result__retry-btn'
													onClick={() => onRetry(index)}
													aria-label={`Retry uploading ${file.name}`}
												>
													<RefreshSVG />
													Retry
												</button>
											)}
										</div>
									)}
								</div>
								<div className='upload-result__preview-actions'>
									{isPreview && (
										<button
											onClick={() => handlePreviewFile?.(index)}
											className='upload-result__preview-btn upload-result__preview-btn--view'
											aria-label={`Preview ${file.name}`}
											type='button'
										>
											<EyeSVG />
										</button>
									)}
									<button
										onClick={() => handleRemoveFile(index)}
										className='upload-result__preview-btn upload-result__preview-btn--remove'
										aria-label={`Remove ${file.name}`}
										type='button'
										disabled={isUploadingFile}
									>
										<TrashSVG />
									</button>
								</div>
							</div>
						);
					})}
				</div>
			</Hvc>

			{/* Error display */}
			{fileError ? (
				<div className='upload-result__error'>
					<TextPrompt
						prompt={fileError}
						status={false}
					/>
				</div>
			) : null}
		</div>
	);
};
